import { useEffect, useState } from 'react';
import { api } from '../lib/api';

/**
 * Las miniaturas de una pantalla, en una sola petición.
 *
 * Cada celda firmaba su propia URL al entrar en pantalla, así que abrir la
 * galería costaba **una petición por foto**: cincuenta miniaturas, cincuenta
 * llamadas a la API. Medido sobre la aplicación compilada: 55 peticiones para
 * pintar una galería de 60 fotos, de las cuales 50 eran firmas. Con un cupo de
 * peticiones por medio —el que tumbó el servicio entero— eso no es una
 * ineficiencia: es la diferencia entre mil visitas al día y veinte mil.
 *
 * La celda sigue pidiendo su miniatura cuando entra en pantalla, que es lo que
 * evita firmar trescientas fotos para enseñar veinte. Lo que cambia es que las
 * peticiones **no salen una a una**: se acumulan durante un instante y viajan
 * juntas. Las que se piden a la vez —las que caben en la pantalla— son una sola
 * llamada.
 *
 * Y lo firmado se guarda hasta poco antes de caducar, así que volver a la
 * galería desde un álbum no vuelve a pedir nada.
 */

/** Cuánto se espera a que se apunten más celdas antes de mandar la tanda. */
const ESPERA_MS = 25;

/** El tope del servidor. Lo que pase de aquí viaja en otra tanda. */
const POR_TANDA = 100;

/**
 * Margen antes de la caducidad real.
 *
 * Una URL que caduca dentro de dos segundos no sirve para una imagen que
 * todavía tiene que descargarse: se considera caducada antes de tiempo.
 */
const MARGEN_MS = 30_000;

interface Guardada {
  url: string;
  caducaEn: number;
}

const cache = new Map<string, Guardada>();
const enEspera = new Map<string, Array<(url: string | null) => void>>();
const cola = new Set<string>();
let programado: ReturnType<typeof setTimeout> | null = null;
let espacioDeLaCola: string | null = null;

const clave = (workspaceId: string, fileId: string) => `${workspaceId}:${fileId}`;

function guardada(workspaceId: string, fileId: string): string | null {
  const entrada = cache.get(clave(workspaceId, fileId));
  if (!entrada) return null;

  if (entrada.caducaEn <= Date.now()) {
    cache.delete(clave(workspaceId, fileId));
    return null;
  }

  return entrada.url;
}

function resolver(workspaceId: string, fileId: string, url: string | null): void {
  const k = clave(workspaceId, fileId);
  for (const avisar of enEspera.get(k) ?? []) avisar(url);
  enEspera.delete(k);
}

/**
 * Manda lo acumulado.
 *
 * Un fallo no se propaga a la pantalla: las miniaturas ya se pintaban «sin
 * imagen» cuando su firma fallaba, y una galería a medias se lee mucho mejor
 * que una pantalla de error por una URL que no se pudo firmar.
 */
async function enviar(workspaceId: string, ids: string[]): Promise<void> {
  try {
    const { urls, expiresAt } = await api<{ urls: Record<string, string>; expiresAt: string }>(
      '/api/files/preview-urls',
      // `lectura`: firmar no cambia nada, así que no puede vaciar la caché de
      // lecturas en cada galería.
      { method: 'POST', body: { workspaceId, fileIds: ids }, lectura: true },
    );

    const caducaEn = new Date(expiresAt).getTime() - MARGEN_MS;

    for (const id of ids) {
      const url = urls[id] ?? null;
      if (url) cache.set(clave(workspaceId, id), { url, caducaEn });
      resolver(workspaceId, id, url);
    }
  } catch {
    for (const id of ids) resolver(workspaceId, id, null);
  }
}

function programar(workspaceId: string): void {
  if (programado) return;

  programado = setTimeout(() => {
    programado = null;
    const ids = [...cola];
    cola.clear();
    espacioDeLaCola = null;

    for (let i = 0; i < ids.length; i += POR_TANDA) {
      void enviar(workspaceId, ids.slice(i, i + POR_TANDA));
    }
  }, ESPERA_MS);
}

/**
 * Pide la miniatura de un archivo. Devuelve la URL, o `null` si no se pudo.
 *
 * Se exporta suelta —y no sólo como hook— porque la usa el `ref` de la celda,
 * que no puede llamar a un hook.
 */
export function pedirMiniatura(workspaceId: string, fileId: string): Promise<string | null> {
  const ya = guardada(workspaceId, fileId);
  if (ya) return Promise.resolve(ya);

  /*
   * Cambiar de espacio a mitad de una tanda mandaría identificadores de un
   * sitio con el `workspaceId` de otro, y el servidor los descartaría en
   * silencio. Se cierra la tanda anterior antes de empezar la nueva.
   */
  if (espacioDeLaCola && espacioDeLaCola !== workspaceId) {
    const previos = [...cola];
    const previo = espacioDeLaCola;
    cola.clear();
    if (previos.length > 0) void enviar(previo, previos);
  }
  espacioDeLaCola = workspaceId;

  return new Promise((resolve) => {
    const k = clave(workspaceId, fileId);
    const lista = enEspera.get(k);
    if (lista) {
      // Otra celda ya la pidió: se espera a la misma respuesta.
      lista.push(resolve);
      return;
    }

    enEspera.set(k, [resolve]);
    cola.add(fileId);
    programar(workspaceId);
  });
}

/** La miniatura de un archivo, para quien la quiera como estado. */
export function useMiniatura(workspaceId: string | null, fileId: string | null): string | null {
  const [url, setUrl] = useState<string | null>(() =>
    workspaceId && fileId ? guardada(workspaceId, fileId) : null,
  );

  useEffect(() => {
    if (!workspaceId || !fileId) return;

    let vigente = true;
    void pedirMiniatura(workspaceId, fileId).then((resultado) => {
      if (vigente) setUrl(resultado);
    });

    return () => {
      vigente = false;
    };
  }, [workspaceId, fileId]);

  return url;
}

/** Sólo para los tests: la caché vive fuera de React y sobrevive al desmontaje. */
export function olvidarMiniaturas(): void {
  cache.clear();
  enEspera.clear();
  cola.clear();
  espacioDeLaCola = null;
  if (programado) clearTimeout(programado);
  programado = null;
}
